// hooks/useReaders.js
import {
  getReadersService,
  getReaderInform,
  resetAllReadersService,
  resetReaderByIdService,
  resetReaderCommerceService,
  sendPulseToAntennaService,
  getAntennaPowerService,
  setAntennaPowerService,
  getAntennaStatusService,
  setAntennaPowerStatusService,
} from "modules/monitoreo/services/readersService";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  readerFlag,
  antennaCount,
  latestReaderUpdate,
  antennaActivity,
} from "../model/readersModel";
import * as XLSX from "xlsx";
import {
  READER_COOLDOWN_KEY,
  readReaderCooldowns,
  readReaderCooldown,
  saveReaderCooldowns,
  saveReaderCooldown,
  removeReaderCooldown,
  withReaderResetLock,
} from "../model/readerCooldowns";
const loadCooldownsForDisplay = () => {
  try {
    return readReaderCooldowns();
  } catch {
    return {};
  }
};

export const useReaders = () => {
  // ============================================
  // ESTADOS PRINCIPALES
  // ============================================
  const fetchRevision = useRef(0);
  const mounted = useRef(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      ++fetchRevision.current;
    };
  }, []);
  const [readerGroups, setReaderGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [resetEDSLoading, setResetEDSLoading] = useState(false);
  const [resetCommerceLoading, setResetCommerceLoading] = useState(false);
  const [resetIndividualLoading, setResetIndividualLoading] = useState(false);
  const [sendingPulse, setSendingPulse] = useState({});
  const [gettingPower, setGettingPower] = useState({});
  const [settingPower, setSettingPower] = useState({});
  const [gettingStatus, setGettingStatus] = useState({});
  const [togglingPower, setTogglingPower] = useState({});
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReader, setSelectedReader] = useState(null);
  const [selectedReaderElement, setSelectedReaderElement] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [resetCooldowns, setResetCooldowns] = useState(loadCooldownsForDisplay);
  const [antennaPowers, setAntennaPowers] = useState({});
  const [antennaStatuses, setAntennaStatuses] = useState({});

  const [edsResetProgress, setEdsResetProgress] = useState({
    current: 0,
    total: 0,
    currentBatch: 0,
    totalBatches: 0,
    status: "idle",
    successCount: 0,
    failedCount: 0,
    currentDevice: null,
  });

  // ============================================
  // FUNCIONES DE NOTIFICACIÓN
  // ============================================
  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // ============================================
  // FUNCIONES DE COOLDOWN
  // ============================================
  const isReaderOnCooldown = useCallback(
    (readerId) => {
      if (!readerId) return false;
      const cooldown = readReaderCooldown(readerId);
      if (!cooldown) return false;
      const now = Date.now();
      const cooldownEnd = cooldown.timestamp + cooldown.duration * 60000;
      return now < cooldownEnd;
    },
    [resetCooldowns]
  );

  const getRemainingCooldownTime = useCallback(
    (readerId) => {
      if (!readerId) return 0;
      const cooldown = readReaderCooldown(readerId);
      if (!cooldown) return 0;
      const now = Date.now();
      const cooldownEnd = cooldown.timestamp + cooldown.duration * 60000;
      const remaining = Math.max(0, cooldownEnd - now);
      return Math.ceil(remaining / 1000);
    },
    [resetCooldowns]
  );

  const getReaderCooldownStatus = useCallback(
    (readerId) => {
      if (!readerId) return null;
      const cooldown = readReaderCooldown(readerId);
      if (!cooldown) return null;
      const now = Date.now();
      const cooldownEnd = cooldown.timestamp + cooldown.duration * 60000;
      const remainingMs = Math.max(0, cooldownEnd - now);
      const remainingMinutes = Math.ceil(remainingMs / 60000);
      const remainingSeconds = Math.ceil(remainingMs / 1000);
      return {
        isActive: remainingMs > 0,
        remainingMinutes,
        remainingSeconds,
        totalMinutes: cooldown.duration,
        timestamp: cooldown.timestamp,
        endsAt: cooldownEnd,
        readerId: readerId,
        readerName: cooldown.readerName,
      };
    },
    [resetCooldowns]
  );

  const startCooldown = useCallback((readerId, readerName = "Reader", durationMinutes = 5) => {
    setResetCooldowns(saveReaderCooldown(readerId, readerName, durationMinutes));
  }, []);

  const startCooldownBatch = useCallback((readers) => {
    setResetCooldowns(saveReaderCooldowns(readers));
  }, []);

  const clearCooldown = useCallback((readerId) => {
    setResetCooldowns(removeReaderCooldown(readerId));
  }, []);

  const getAllCooldownReaders = useCallback(() => {
    const now = Date.now();
    const activeCooldowns = [];
    Object.entries(resetCooldowns).forEach(([readerId, cooldown]) => {
      const cooldownEnd = cooldown.timestamp + cooldown.duration * 60000;
      if (now < cooldownEnd) {
        activeCooldowns.push({
          readerId,
          ...cooldown,
          remainingMs: cooldownEnd - now,
          remainingMinutes: Math.ceil((cooldownEnd - now) / 60000),
          remainingSeconds: Math.ceil((cooldownEnd - now) / 1000),
        });
      }
    });
    return activeCooldowns;
  }, [resetCooldowns]);

  useEffect(() => {
    const sync = (event) => {
      if (event?.type === "storage" && event.key !== READER_COOLDOWN_KEY && event.key !== null)
        return;
      const next = loadCooldownsForDisplay();
      setResetCooldowns((previous) =>
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next
      );
    };
    // Refresh remaining time, even while the stored entries themselves are unchanged.
    const timer = setInterval(() => setResetCooldowns(loadCooldownsForDisplay()), 1000);
    window.addEventListener("storage", sync);
    window.addEventListener("goper:reader-cooldown", sync);
    window.addEventListener("focus", sync);
    sync();
    return () => {
      clearInterval(timer);
      window.removeEventListener("storage", sync);
      window.removeEventListener("goper:reader-cooldown", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  // ============================================
  // FUNCIONES AUXILIARES PARA ANTENAS
  // ============================================
  const findReaderByAntennaId = useCallback(
    (antennaId) => {
      for (const group of readerGroups) {
        for (const reader of group.readers) {
          const foundAntenna = reader.antennas?.find(
            (antenna) => antenna.id === antennaId.toString() || antenna.id === antennaId
          );
          if (foundAntenna) {
            return reader;
          }
        }
      }
      return null;
    },
    [readerGroups]
  );

  const checkAntennaCooldown = useCallback(
    (antennaId) => {
      const reader = findReaderByAntennaId(antennaId);
      if (!reader || !reader.id) return null;

      const isOnCooldown = isReaderOnCooldown(reader.id);
      if (!isOnCooldown) return null;

      const remainingSeconds = getRemainingCooldownTime(reader.id);
      const remainingMinutes = Math.ceil(remainingSeconds / 60);

      return {
        readerId: reader.id,
        readerName: reader.hostname,
        antennaId,
        isOnCooldown: true,
        remainingMinutes,
        remainingSeconds,
      };
    },
    [findReaderByAntennaId, isReaderOnCooldown, getRemainingCooldownTime]
  );

  // ============================================
  // FUNCIONES DE EXPORTACIÓN
  // ============================================
  const exportToExcel = useCallback(
    async (exportAll = false, groups = readerGroups) => {
      setExporting(true);
      try {
        let dataToExport = [];
        if (exportAll) {
          const result = await getReaderInform();
          if (result.success && result.data) {
            dataToExport = result.data;
          } else {
            throw new Error(result.message || "No se pudieron obtener los datos para exportar");
          }
        } else {
          const allReaders = groups.flatMap((group) => group.readers);
          dataToExport = allReaders.map((reader) => ({
            id: reader.id,
            hostId: reader.hostId,
            hostname: reader.hostname,
            ip: reader.ip,
            commerce: reader.commerce,
            status: reader.status ? "ACTIVO" : "INACTIVO",
            antennas: reader.antennas?.length || 0,
            updatedAt: reader.updatedAt,
            createdAt: reader.createdAt,
            more: reader.more,
            group: groups.find((g) => g.readers.includes(reader))?.name || "Sin grupo",
          }));
        }

        if (dataToExport.length === 0) {
          showNotification("⚠️ No hay datos para exportar", "warning");
          return null;
        }

        const worksheetData = dataToExport.map((item) => ({
          ID: item.id,
          "ID Host": item.idhost || item.hostId,
          "Nombre del Reader": item.hostname,
          "Dirección IP": item.ip,
          Comercio: item.commerce,
          Estado: readerFlag(item.status) ? "ACTIVO" : "INACTIVO",
          "N° Antenas": antennaCount(item.antenna ?? item.antennas),
          "Última Actualización": item.updatedAt || item.createdAt,
          "Fecha Creación": item.createdAt,
          "Reset Date": item.more?.resetdate || "N/A",
          "Reset Response": item.more?.resetresponse || "N/A",
          Grupo: item.group || "N/A",
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const colWidths = [
          { wch: 10 },
          { wch: 15 },
          { wch: 30 },
          { wch: 15 },
          { wch: 25 },
          { wch: 12 },
          { wch: 12 },
          { wch: 20 },
          { wch: 20 },
          { wch: 20 },
          { wch: 25 },
          { wch: 20 },
        ];
        worksheet["!cols"] = colWidths;
        XLSX.utils.book_append_sheet(workbook, worksheet, "Readers Report");

        const summaryData = [
          ["REPORTE DE READERS - RESUMEN"],
          ["Fecha de generación:", new Date().toLocaleString()],
          ["Total de readers:", dataToExport.length],
          ["Readers activos:", dataToExport.filter((r) => readerFlag(r.status)).length],
          ["Readers inactivos:", dataToExport.filter((r) => !readerFlag(r.status)).length],
          ["Total de comercios:", new Set(dataToExport.map((r) => r.commerce)).size],
          [
            "Total de antenas:",
            dataToExport.reduce((sum, r) => sum + antennaCount(r.antenna ?? r.antennas), 0),
          ],
        ];

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Resumen");

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        const fileName = `Readers_Report_${timestamp}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        showNotification(
          `✅ Informe exportado exitosamente: ${dataToExport.length} registros`,
          "success"
        );
        return {
          success: true,
          fileName,
          recordsCount: dataToExport.length,
          timestamp: new Date().toISOString(),
        };
      } catch (error) {
        console.error("❌ Error exportando a Excel:", error);
        showNotification(`❌ Error al exportar: ${error.message}`, "error");
        return { success: false, error: error.message };
      } finally {
        setExporting(false);
      }
    },
    [readerGroups, showNotification]
  );

  const exportAdvancedReport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await getReaderInform();
      if (!result.success || !result.data || result.data.length === 0) {
        showNotification(result.message || "⚠️ No hay datos para exportar", "warning");
        return null;
      }

      const data = result.data;
      const workbook = XLSX.utils.book_new();
      const mainSheetData = data.map((item) => ({
        ID: item.id,
        "ID Host": item.idhost,
        Nombre: item.hostname,
        IP: item.ip,
        Comercio: item.commerce,
        Estado: item.status ? "ACTIVO" : "INACTIVO",
        Antenas: item.antenna,
        Creado: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A",
        Actualizado: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "N/A",
        "Último Reset": item.more?.resetdate || "N/A",
        "Respuesta Reset": item.more?.resetresponse || "N/A",
      }));

      const mainSheet = XLSX.utils.json_to_sheet(mainSheetData);
      XLSX.utils.book_append_sheet(workbook, mainSheet, "Todos los Readers");

      const commerceGroups = {};
      data.forEach((item) => {
        const commerce = item.commerce || "Sin comercio";
        if (!commerceGroups[commerce]) {
          commerceGroups[commerce] = [];
        }
        commerceGroups[commerce].push(item);
      });

      Object.entries(commerceGroups).forEach(([commerce, items]) => {
        const sheetData = items.map((item) => ({
          Nombre: item.hostname,
          IP: item.ip,
          Estado: item.status ? "ACTIVO" : "INACTIVO",
          Antenas: item.antenna,
          "Último Reset": item.more?.resetdate || "N/A",
        }));
        const sheetName = commerce.length > 31 ? commerce.substring(0, 28) + "..." : commerce;
        const sheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
      });

      const totalReaders = data.length;
      const activeReaders = data.filter((r) => r.status).length;
      const inactiveReaders = totalReaders - activeReaders;
      const totalCommerces = Object.keys(commerceGroups).length;
      const totalAntennas = data.reduce((sum, r) => sum + (parseInt(r.antenna) || 0), 0);
      const activePercentage =
        totalReaders > 0 ? ((activeReaders / totalReaders) * 100).toFixed(2) : 0;

      const summaryData = [
        ["REPORTE ESTADÍSTICO DE READERS"],
        [],
        ["Fecha de generación:", new Date().toLocaleString()],
        ["Generado por:", "Sistema de Automatización"],
        [],
        ["ESTADÍSTICAS GENERALES"],
        ["Total de Readers:", totalReaders],
        ["Readers Activos:", activeReaders],
        ["Readers Inactivos:", inactiveReaders],
        ["Porcentaje de Activos:", `${activePercentage}%`],
        ["Total de Comercios:", totalCommerces],
        ["Total de Antenas:", totalAntennas],
        [
          "Promedio de Antenas por Reader:",
          totalReaders > 0 ? (totalAntennas / totalReaders).toFixed(2) : 0,
        ],
        [],
        ["DISTRIBUCIÓN POR COMERCIO"],
        ...Object.entries(commerceGroups).map(([commerce, items]) => [
          commerce,
          items.length,
          items.filter((r) => r.status).length,
          items.filter((r) => !r.status).length,
          `${
            items.length > 0
              ? ((items.filter((r) => r.status).length / items.length) * 100).toFixed(2)
              : 0
          }%`,
        ]),
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Estadísticas");

      const problematicReaders = data.filter(
        (item) =>
          !item.status ||
          (item.more?.resetresponse &&
            !item.more.resetresponse.includes("éxito") &&
            !item.more.resetresponse.includes("pulso"))
      );

      if (problematicReaders.length > 0) {
        const problemsSheetData = problematicReaders.map((item) => ({
          Nombre: item.hostname,
          IP: item.ip,
          Comercio: item.commerce,
          Problema: !item.status ? "INACTIVO" : item.more?.resetresponse || "Error desconocido",
          "Último Reset": item.more?.resetdate || "Nunca",
          Desde: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A",
        }));
        const problemsSheet = XLSX.utils.json_to_sheet(problemsSheetData);
        XLSX.utils.book_append_sheet(workbook, problemsSheet, "Readers con Problemas");
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const fileName = `Reporte_Avanzado_Readers_${timestamp}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showNotification(
        `✅ Reporte avanzado exportado: ${totalReaders} readers en ${totalCommerces} comercios`,
        "success"
      );
      return {
        success: true,
        fileName,
        totalReaders,
        totalCommerces,
        activeReaders,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("❌ Error exportando reporte avanzado:", error);
      showNotification(`❌ Error al exportar reporte: ${error.message}`, "error");
      return { success: false, error: error.message };
    } finally {
      setExporting(false);
    }
  }, [showNotification]);

  // ============================================
  // FUNCIONES DE CONVERSIÓN DE POTENCIA
  // ============================================
  const convertDBmToMW = useCallback((dbm) => {
    if (dbm === undefined || dbm === null) return 0;
    const numericValue = parseFloat(dbm);
    if (isNaN(numericValue)) return 0;
    return Math.round(numericValue * 100);
  }, []);

  const formatMWToDBm = useCallback((mW) => {
    if (!mW) return "0.0";
    const dbm = mW / 100;
    return dbm.toFixed(1);
  }, []);

  // ============================================
  // FUNCIONES DE MAPEO DE DATOS
  // ============================================
  const mapAntennaData = useCallback(
    (antenna) => {
      if (!antenna) return null;

      const portMatch = antenna.point?.match(/\d+/);
      const port = portMatch ? parseInt(portMatch[0]) : 0;
      const status = antennaActivity(antenna.status);
      const signalStrength = antenna.signalStrength ?? "Sin información";

      let power = null;
      let formattedPower = null;

      if (antenna.id && antennaPowers[antenna.id]) {
        power = antennaPowers[antenna.id].power;
        formattedPower = antennaPowers[antenna.id].formattedPower;
      } else if (
        antenna.more?.power !== null &&
        antenna.more?.power !== undefined &&
        antenna.more.power !== ""
      ) {
        const rawPower = parseInt(antenna.more.power);
        if (!isNaN(rawPower)) {
          power = rawPower;
          formattedPower = formatMWToDBm(rawPower);
        }
      }

      return {
        id: antenna.id?.toString() || `ant-${Date.now()}`,
        name: antenna.point || "Antena sin nombre",
        port: port,
        status: status,
        model: antenna.model || "Sin información",
        frequency: antenna.frequency || "Sin información",
        power: power,
        formattedPower: formattedPower,
        lastUpdate: antenna.updateat || antenna.createdat,
        lastPulse: antenna.more?.resetdate || null,
        signalStrength: signalStrength,
        statusFromApi: null,
        rawData: antenna,
      };
    },
    [antennaPowers, formatMWToDBm]
  );

  const mapReaderData = useCallback(
    (apiData) => {
      if (!apiData || !Array.isArray(apiData)) {
        console.error("Datos API inválidos:", apiData);
        return [];
      }

      return apiData
        .filter((group) => group && typeof group === "object" && Array.isArray(group.elements))
        .map((group, index) => {
          const readers = group.elements
            .filter((reader) => reader && typeof reader === "object")
            .map((reader, readerIndex) => {
              const antennaArray = reader.antenna || [];
              const antennas = Array.isArray(antennaArray)
                ? antennaArray.map(mapAntennaData).filter(Boolean)
                : [];

              const readerId = reader.id?.toString() || `reader-${index}-${readerIndex}`;

              const hasPulseAPI = Boolean(reader.pulse_api);

              return {
                id: readerId,
                hostId: reader.idhost?.toString() || null,
                hostname: reader.hostname || `Reader ${readerIndex + 1}`,
                ip: reader.ip || "Sin IP",
                commerce: reader.commerce || group.hostname,
                status: readerFlag(reader.status),
                more: reader.more || null,
                pulse_api: hasPulseAPI,
                reader_type: reader.reader_type || "Estándar",
                createdAt: reader.createdAt,
                updatedAt: reader.updatedAt,
                deletedAt: reader.deletedAt,
                antennas: antennas,
                rawAntennaData: antennaArray,
                rawData: reader,
                isOnCooldown: false,
                cooldownInfo: null,
              };
            });

          const activeReaders = readers.filter((reader) => reader.status).length;

          return {
            id: `group-${index}`,
            name: (group.hostname || `Grupo ${index + 1}`).trim(),
            hostname: group.hostname || `Grupo ${index + 1}`,
            totalReaders: readers.length,
            activeReaders,
            readers,
            elements: group.elements,
            lastUpdate: latestReaderUpdate(readers),
          };
        });
    },
    [mapAntennaData]
  );

  // ============================================
  // FUNCIONES PRINCIPALES DE DATOS
  // ============================================
  const fetchReaders = useCallback(async () => {
    if (!mounted.current) return null;
    const revision = ++fetchRevision.current;
    setLoading(true);
    setError(null);

    try {
      const response = await getReadersService();
      if (revision !== fetchRevision.current) return null;

      if (!response || !response.data || !Array.isArray(response.data)) {
        throw new Error("Formato de datos inválido");
      }

      const mappedData = mapReaderData(response.data);

      const updatedReaderGroups = mappedData.map((group) => ({
        ...group,
        readers: group.readers.map((reader) => ({
          ...reader,
          isOnCooldown: isReaderOnCooldown(reader.id),
          cooldownInfo: getReaderCooldownStatus(reader.id),
        })),
      }));

      setReaderGroups(updatedReaderGroups);
      setLastFetchedAt(new Date().toISOString());

      const totalReaders = mappedData.reduce((sum, group) => sum + group.totalReaders, 0);
      const totalAntennas = mappedData.reduce((sum, group) => {
        return (
          sum +
          group.readers.reduce((readerSum, reader) => {
            return readerSum + (reader.antennas?.length || 0);
          }, 0)
        );
      }, 0);

      showNotification(
        `Se cargaron ${mappedData.length} grupos con ${totalReaders} readers y ${totalAntennas} antenas`,
        "success"
      );

      return updatedReaderGroups;
    } catch (err) {
      if (revision !== fetchRevision.current) return null;

      let errorMessage = "Error al cargar los readers";
      if ((err.message || "").includes("token")) {
        errorMessage = "Sesión expirada. Por favor, inicie sesión nuevamente.";
      } else if ((err.message || "").includes("Formato")) {
        errorMessage = "Error en el formato de datos recibidos";
      } else {
        errorMessage = err.message || "Error desconocido";
      }

      setError(errorMessage);
      showNotification(errorMessage, "error");
      return null;
    } finally {
      if (revision === fetchRevision.current) setLoading(false);
    }
  }, [showNotification, mapReaderData, isReaderOnCooldown, getReaderCooldownStatus]);

  useEffect(() => {
    if (readerGroups.length > 0) {
      const updatedReaderGroups = readerGroups.map((group) => ({
        ...group,
        readers: group.readers.map((reader) => ({
          ...reader,
          isOnCooldown: isReaderOnCooldown(reader.id),
          cooldownInfo: getReaderCooldownStatus(reader.id),
        })),
      }));

      if (JSON.stringify(updatedReaderGroups) !== JSON.stringify(readerGroups)) {
        setReaderGroups(updatedReaderGroups);
      }
    }
  }, [resetCooldowns, readerGroups, isReaderOnCooldown, getReaderCooldownStatus]);

  // ============================================
  // FUNCIONES DE REINICIO
  // ============================================
  const resetAllReaders = useCallback(async () => {
    const targets = readerGroups.flatMap((group) => group.readers);
    if (!targets.length)
      return { success: false, error: "Consulta los readers antes de reiniciarlos." };
    if (targets.some((reader) => isReaderOnCooldown(reader.id))) {
      return {
        success: false,
        cooldown: true,
        error:
          "Hay readers en espera. El reinicio masivo no permite excluirlos; espera a que se cumplan los 5 minutos.",
      };
    }
    setResetAllLoading(true);

    try {
      startCooldownBatch(targets);
      const response = await resetAllReadersService();

      if (response.success) {
        startCooldownBatch(targets);
        showNotification("Reinicio masivo de readers iniciado correctamente", "success");

        setTimeout(() => {
          fetchReaders();
        }, 2000);

        return {
          ...response,
          success: true,
          message: response.message || "Solicitud de reinicio recibida",
        };
      } else {
        throw new Error(response.message || "Error en el reinicio masivo");
      }
    } catch (error) {
      console.error("Error al reiniciar todos los readers:", error);
      showNotification(`Error: ${error.message || "No se pudo reiniciar los readers"}`, "error");
      return { success: false, error: error.message };
    } finally {
      setResetAllLoading(false);
    }
  }, [showNotification, fetchReaders, readerGroups, isReaderOnCooldown, startCooldownBatch]);

  const resetEDSReaders = useCallback(
    async (batchSize = 10, batchDelay = 2000) => {
      const activeEDSReaders = readerGroups
        .filter((group) => group.name?.toUpperCase().startsWith("EDS"))
        .flatMap((group) => group.readers)
        .filter((reader) => reader.status === true);
      const waiting = activeEDSReaders.filter((reader) => isReaderOnCooldown(reader.id));
      const eligibleEDSReaders = activeEDSReaders.filter(
        (reader) => !isReaderOnCooldown(reader.id)
      );

      const totalEDSReadersActive = eligibleEDSReaders.length;
      const totalEDSReadersTotal = readerGroups
        .filter((group) => group.name?.toUpperCase().startsWith("EDS"))
        .reduce((sum, group) => sum + group.totalReaders, 0);
      const totalEDSInactive = totalEDSReadersTotal - activeEDSReaders.length;

      if (totalEDSReadersActive === 0) {
        showNotification(
          `⚠️ No hay comercios EDS activos para reiniciar. (${totalEDSReadersTotal} totales, ${totalEDSInactive} inactivos)`,
          "warning"
        );
        setEdsResetProgress({
          current: 0,
          total: 0,
          currentBatch: 0,
          totalBatches: 0,
          status: "error",
          successCount: 0,
          failedCount: 0,
          currentDevice: null,
        });
        return {
          success: false,
          cooldown: waiting.length > 0,
          error: waiting.length
            ? "Los readers EDS activos están en espera de 5 minutos."
            : "No hay comercios EDS activos",
          totalEDSReadersActive,
          totalEDSReadersTotal,
          totalEDSInactive,
        };
      }

      setResetEDSLoading(true);

      setEdsResetProgress({
        current: 0,
        total: totalEDSReadersActive,
        currentBatch: 0,
        totalBatches: Math.ceil(totalEDSReadersActive / batchSize),
        status: "processing",
        successCount: 0,
        failedCount: 0,
        currentDevice: null,
      });

      const results = {
        success: [],
        failed: [],
        total: totalEDSReadersActive,
        startTime: Date.now(),
      };

      try {
        showNotification(
          `🔄 Iniciando reinicio de ${totalEDSReadersActive} dispositivos EDS activos en lotes de ${batchSize}...`,
          "info"
        );

        const totalBatches = Math.ceil(totalEDSReadersActive / batchSize);

        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          const start = batchIndex * batchSize;
          const end = Math.min(start + batchSize, totalEDSReadersActive);
          const batch = eligibleEDSReaders.slice(start, end);
          const currentBatchNumber = batchIndex + 1;

          setEdsResetProgress((prev) => ({
            ...prev,
            currentBatch: currentBatchNumber,
            currentDevice: `Procesando lote ${currentBatchNumber}/${totalBatches} (${batch.length} dispositivos)`,
          }));

          showNotification(
            `📦 Procesando lote ${currentBatchNumber}/${totalBatches} (${batch.length} dispositivos)...`,
            "info"
          );

          const batchPromises = batch.map(async (reader, idx) => {
            const globalIndex = start + idx + 1;

            try {
              if (isReaderOnCooldown(reader.id))
                return {
                  success: false,
                  reader,
                  error: "Reader en espera",
                  duration: 0,
                  globalIndex,
                };
              if (!reader.hostId)
                return {
                  success: false,
                  reader,
                  error: "Reader sin ID host",
                  duration: 0,
                  globalIndex,
                };
              startCooldown(reader.id, reader.hostname, 5);
              const response = await resetReaderByIdService(reader.hostId);

              if (response.success) {
                startCooldown(reader.id, reader.hostname, 5);
                return {
                  success: true,
                  reader,
                  response,
                  globalIndex,
                  duration: response.duration,
                };
              } else {
                throw new Error(response.message || "Error en reinicio");
              }
            } catch (error) {
              return {
                success: false,
                reader,
                error: error.message,
                globalIndex,
              };
            }
          });

          const batchResults = await Promise.all(batchPromises);

          batchResults.forEach((result) => {
            if (result.success) {
              results.success.push({
                id: result.reader.id,
                hostId: result.reader.hostId,
                hostname: result.reader.hostname,
                commerce: result.reader.commerce,
                duration: result.duration,
                globalIndex: result.globalIndex,
              });

              setEdsResetProgress((prev) => ({
                ...prev,
                current: prev.current + 1,
                successCount: prev.successCount + 1,
                currentDevice: `✅ ${result.reader.hostname} (${
                  prev.current + 1
                }/${totalEDSReadersActive})`,
              }));

              showNotification(
                `✅ [${result.globalIndex}/${totalEDSReadersActive}] ${result.reader.hostname} reiniciado`,
                "success"
              );
            } else {
              results.failed.push({
                id: result.reader.id,
                hostname: result.reader.hostname,
                error: result.error,
                globalIndex: result.globalIndex,
              });

              setEdsResetProgress((prev) => ({
                ...prev,
                current: prev.current + 1,
                failedCount: prev.failedCount + 1,
                currentDevice: `❌ ${result.reader.hostname} falló: ${result.error}`,
              }));

              showNotification(
                `❌ [${result.globalIndex}/${totalEDSReadersActive}] ${result.reader.hostname}: ${result.error}`,
                "error"
              );
            }
          });

          if (batchIndex < totalBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, batchDelay));
          }
        }

        const endTime = Date.now();
        const totalDuration = Math.round((endTime - results.startTime) / 1000);
        const minutes = Math.floor(totalDuration / 60);
        const seconds = totalDuration % 60;
        const durationFormatted = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

        if (results.failed.length === 0) {
          showNotification(
            `🎉 Reinicio EDS completado: ${results.success.length} dispositivos reiniciados exitosamente en ${durationFormatted}`,
            "success"
          );
          setEdsResetProgress((prev) => ({
            ...prev,
            status: "completed",
          }));
        } else {
          showNotification(
            `⚠️ Reinicio EDS completado: ${results.success.length} éxitos, ${results.failed.length} fallidos en ${durationFormatted}`,
            "warning"
          );
          setEdsResetProgress((prev) => ({
            ...prev,
            status: "completed",
          }));
        }

        setTimeout(() => {
          fetchReaders();
          setTimeout(() => {
            setEdsResetProgress({
              current: 0,
              total: 0,
              currentBatch: 0,
              totalBatches: 0,
              status: "idle",
              successCount: 0,
              failedCount: 0,
              currentDevice: null,
            });
          }, 5000);
        }, 2000);

        const edsGroups = readerGroups
          .filter((group) => group.name?.toUpperCase().startsWith("EDS"))
          .map((group) => ({
            name: group.name,
            active: group.activeReaders,
            total: group.totalReaders,
            inactive: group.totalReaders - group.activeReaders,
          }));

        return {
          success: results.failed.length === 0,
          message: `Reinicio EDS completado: ${results.success.length} exitosos, ${results.failed.length} fallidos, ${waiting.length} omitidos por espera`,
          skippedCooldownCount: waiting.length,
          stats: {
            totalProcessed: results.success.length + results.failed.length,
            successCount: results.success.length,
            failedCount: results.failed.length,
            skippedCount: totalEDSInactive + waiting.length,
          },
          results,
          totalDuration,
          durationFormatted,
          totalEDSReadersActive,
          totalEDSReadersTotal,
          totalEDSInactive,
          edsGroups,
          timestamp: new Date().toISOString(),
          processedCount: results.success.length,
          failedCount: results.failed.length,
        };
      } catch (error) {
        console.error("Error en reinicio EDS:", error);
        showNotification(`❌ Error en reinicio EDS: ${error.message}`, "error");
        setEdsResetProgress((prev) => ({
          ...prev,
          status: "error",
        }));
        return {
          success: false,
          error: error.message,
          results,
          totalEDSReadersActive,
          totalEDSReadersTotal,
          totalEDSInactive,
        };
      } finally {
        setResetEDSLoading(false);
      }
    },
    [readerGroups, showNotification, fetchReaders, startCooldown, isReaderOnCooldown]
  );

  const resetReaderByCommerce = useCallback(
    async (commerce) => {
      if (!commerce || typeof commerce !== "string") {
        showNotification("Error: Comercio inválido", "error");
        return { success: false, error: "Comercio inválido" };
      }

      const targets = readerGroups
        .flatMap((group) => group.readers)
        .filter((reader) => reader.commerce === commerce);
      if (!targets.length)
        return { success: false, error: "No hay readers consultados para este comercio." };
      if (targets.some((reader) => isReaderOnCooldown(reader.id))) {
        return {
          success: false,
          cooldown: true,
          error:
            "Hay readers del comercio en espera. Este reinicio no permite excluirlos; espera a que se cumplan los 5 minutos.",
        };
      }
      setResetCommerceLoading(true);

      try {
        startCooldownBatch(targets);
        const response = await resetReaderCommerceService(commerce);

        if (response.success) {
          startCooldownBatch(targets);
          showNotification(`Readers del comercio ${commerce} reiniciados correctamente`, "success");

          setTimeout(() => {
            fetchReaders();
          }, 1000);

          return {
            ...response,
            success: true,
            message: response.message || "Solicitud de reinicio recibida",
            commerce: commerce,
            timestamp: response.timestamp,
            failedCount: response.failedCount || 0,
          };
        } else {
          throw new Error(response.message || "Error al reiniciar los readers del comercio");
        }
      } catch (error) {
        console.error(`Error al reiniciar readers del comercio ${commerce}:`, error);
        showNotification(
          `Error: ${error.message || "No se pudo reiniciar los readers del comercio"}`,
          "error"
        );
        return { success: false, error: error.message, commerce: commerce, message: error.message };
      } finally {
        setResetCommerceLoading(false);
      }
    },
    [showNotification, fetchReaders, readerGroups, isReaderOnCooldown, startCooldownBatch]
  );

  const resetReaderById = useCallback(
    async (reader) => {
      if (!reader || !reader.id || !reader.hostId) {
        showNotification("Error: Reader inválido", "error");
        return { success: false, error: "Reader inválido" };
      }

      if (isReaderOnCooldown(reader.id)) {
        const remainingSeconds = getRemainingCooldownTime(reader.id);
        const remainingMinutes = Math.ceil(remainingSeconds / 60);

        showNotification(
          `El reader "${reader.hostname}" está en tiempo de espera. Espere ${remainingMinutes} minutos antes de reiniciarlo nuevamente.`,
          "warning"
        );

        return {
          success: false,
          error: "Device on cooldown",
          cooldown: true,
          remainingMinutes,
          readerId: reader.id,
          readerName: reader.hostname,
        };
      }

      setResetIndividualLoading(true);

      try {
        startCooldown(reader.id, reader.hostname, 5);
        const response = await resetReaderByIdService(reader.hostId);

        if (response.success) {
          startCooldown(reader.id, reader.hostname, 5);

          showNotification(
            `Reader "${reader.hostname}" reiniciado correctamente. No podrá reiniciar este dispositivo por 5 minutos.`,
            "success"
          );

          setReaderGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              readers: group.readers.map((r) =>
                r.id === reader.id
                  ? {
                      ...r,
                      isOnCooldown: true,
                      cooldownInfo: getReaderCooldownStatus(reader.id),
                    }
                  : r
              ),
            }))
          );

          setTimeout(() => {
            fetchReaders();
          }, 2000);

          return {
            ...response,
            success: true,
            message: response.message || "Solicitud de reinicio recibida",
            cooldownStarted: true,
            readerId: reader.id,
            readerHostId: reader.hostId,
            readerName: reader.hostname,
            timestamp: new Date().toISOString(),
          };
        } else {
          throw new Error(response.message || "Error al reiniciar el reader");
        }
      } catch (error) {
        console.error(`Error al reiniciar reader ${reader.id}:`, error);
        showNotification(`Error: ${error.message || "No se pudo reiniciar el reader"}`, "error");
        return { success: false, error: error.message };
      } finally {
        setResetIndividualLoading(false);
      }
    },
    [
      showNotification,
      fetchReaders,
      isReaderOnCooldown,
      getRemainingCooldownTime,
      startCooldown,
      getReaderCooldownStatus,
    ]
  );

  // ============================================
  // FUNCIONES DE PULSO A ANTENAS
  // ============================================
  const handleSendPulse = useCallback(
    async (antennaId, duration = null) => {
      if (!antennaId) {
        showNotification("Error: ID de antena no válido", "error");
        return { success: false, error: "ID de antena no válido" };
      }

      const cooldownInfo = checkAntennaCooldown(antennaId);
      if (cooldownInfo) {
        showNotification(
          `El reader "${cooldownInfo.readerName}" está en tiempo de espera (reinicio reciente). Espere ${cooldownInfo.remainingMinutes} minutos antes de enviar pulsos.`,
          "warning"
        );
        return {
          success: false,
          error: "Reader on cooldown",
          cooldown: true,
          remainingMinutes: cooldownInfo.remainingMinutes,
          readerId: cooldownInfo.readerId,
          readerName: cooldownInfo.readerName,
          antennaId,
        };
      }

      setSendingPulse((prev) => ({ ...prev, [antennaId]: true }));

      try {
        const response = await sendPulseToAntennaService(antennaId, duration);

        if (response.success) {
          showNotification(
            `Pulso enviado a antena con ID: ${antennaId} correctamente - ${response.resetResponse}` +
              (response.requestedDuration ? ` (Duración: ${response.requestedDuration}ms)` : ""),
            "success"
          );

          setReaderGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              readers: group.readers.map((reader) => ({
                ...reader,
                antennas: reader.antennas?.map((antenna) =>
                  antenna.id === antennaId.toString()
                    ? {
                        ...antenna,
                        lastPulse: response.resetDate,
                        status: "Activa",
                        rawData: {
                          ...antenna.rawData,
                          more: {
                            ...antenna.rawData?.more,
                            resetdate: response.resetDate,
                            resetresponse: response.resetResponse,
                            duration: response.requestedDuration,
                          },
                        },
                      }
                    : antenna
                ),
              })),
            }))
          );

          return {
            success: true,
            message: response.message,
            antennaId: response.antennaId,
            resetDate: response.resetDate,
            resetResponse: response.resetResponse,
            requestedDuration: response.requestedDuration,
          };
        } else {
          throw new Error(response.message || "Error al enviar pulso");
        }
      } catch (error) {
        console.error(`Error al enviar pulso a antena ${antennaId}:`, error);

        let errorMessage = error.message || "No se pudo enviar el pulso";
        if (error.message.includes("token") || errorMessage.includes("autenticación")) {
          errorMessage = "Error de autenticación: " + errorMessage;
        } else if (errorMessage.includes("conexión") || errorMessage.includes("network")) {
          errorMessage = "Error de conexión: " + errorMessage;
        } else if (errorMessage.includes("no encontrada") || errorMessage.includes("404")) {
          errorMessage = "Antena no encontrada: " + errorMessage;
        }

        showNotification(`Error: ${errorMessage}`, "error");
        return { success: false, error: errorMessage };
      } finally {
        setSendingPulse((prev) => ({ ...prev, [antennaId]: false }));
      }
    },
    [showNotification, checkAntennaCooldown]
  );

  const handleSendPulseToMultiple = useCallback(
    async (antennaIds, duration = null) => {
      if (!Array.isArray(antennaIds) || antennaIds.length === 0) {
        showNotification("Error: No se proporcionaron antenas válidas", "error");
        return { success: false, error: "IDs de antenas no válidos" };
      }

      const antennasOnCooldown = [];
      const validAntennaIds = [];

      for (const antennaId of antennaIds) {
        const cooldownInfo = checkAntennaCooldown(antennaId);
        if (cooldownInfo) {
          antennasOnCooldown.push(cooldownInfo);
        } else {
          validAntennaIds.push(antennaId);
        }
      }

      if (antennasOnCooldown.length > 0) {
        const readerNames = [...new Set(antennasOnCooldown.map((a) => a.readerName))];
        showNotification(
          `⚠️ ${
            antennasOnCooldown.length
          } antena(s) no se procesarán porque pertenecen a readers en tiempo de espera (${readerNames.join(
            ", "
          )})`,
          "warning"
        );
      }

      if (validAntennaIds.length === 0) {
        return {
          success: false,
          error: "Todas las antenas están en readers con cooldown",
          antennasOnCooldown,
        };
      }

      const sendingState = {};
      validAntennaIds.forEach((id) => {
        sendingState[id] = true;
      });

      setSendingPulse((prev) => ({ ...prev, ...sendingState }));

      try {
        const responses = await Promise.allSettled(
          validAntennaIds.map((id) => sendPulseToAntennaService(id, duration))
        );

        const successCount = responses.filter((r) => r.status === "fulfilled").length;
        const failedCount = responses.filter((r) => r.status === "rejected").length;

        if (successCount > 0) {
          showNotification(
            `Pulsos enviados: ${successCount} exitosos, ${failedCount} fallidos${
              antennasOnCooldown.length > 0
                ? `, ${antennasOnCooldown.length} omitidos (cooldown)`
                : ""
            }` + (duration ? ` (Duración: ${duration}ms)` : ""),
            successCount === validAntennaIds.length ? "success" : "warning"
          );
        } else {
          showNotification("No se pudo enviar ningún pulso", "error");
        }

        setTimeout(() => {
          fetchReaders();
        }, 1500);

        return {
          success: successCount > 0,
          results: responses.map((r, idx) => ({
            antennaId: validAntennaIds[idx],
            status: r.status,
            value: r.value,
            reason: r.reason,
          })),
          antennasOnCooldown,
          successCount,
          failedCount,
          requestedDuration: duration,
        };
      } catch (error) {
        console.error("Error al enviar pulsos múltiples:", error);
        showNotification(`Error: ${error.message || "Error en operación múltiple"}`, "error");
        return { success: false, error: error.message };
      } finally {
        const clearState = {};
        antennaIds.forEach((id) => {
          clearState[id] = false;
        });
        setSendingPulse((prev) => ({ ...prev, ...clearState }));
      }
    },
    [showNotification, fetchReaders, checkAntennaCooldown]
  );

  // ============================================
  // FUNCIONES DE MANEJO DE POTENCIA DE ANTENAS
  // ============================================
  const getAntennaPower = useCallback(
    async (antennaId) => {
      if (!antennaId) {
        showNotification("❌ ID de antena no válido", "error");
        return { success: false, error: "ID de antena no válido" };
      }

      const cooldownInfo = checkAntennaCooldown(antennaId);
      if (cooldownInfo) {
        showNotification(
          `El reader "${cooldownInfo.readerName}" está en tiempo de espera. Espere ${cooldownInfo.remainingMinutes} minutos antes de operar sus antenas.`,
          "warning"
        );

        return {
          success: false,
          error: "Reader on cooldown",
          cooldown: true,
          readerId: cooldownInfo.readerId,
          readerName: cooldownInfo.readerName,
        };
      }

      const antennaIdStr = antennaId.toString();
      setGettingPower((prev) => ({ ...prev, [antennaIdStr]: true }));

      try {
        const response = await getAntennaPowerService(antennaId);

        if (response.success) {
          const powerInMW = response.power || 0;
          const formattedPower = formatMWToDBm(powerInMW);

          setAntennaPowers((prev) => ({
            ...prev,
            [antennaIdStr]: {
              power: powerInMW,
              formattedPower: formattedPower,
              timestamp: response.timestamp,
              antennaId: response.antennaId,
              message: response.message,
            },
          }));

          setReaderGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              readers: group.readers.map((reader) => ({
                ...reader,
                antennas: reader.antennas?.map((antenna) =>
                  antenna.id === antennaIdStr
                    ? {
                        ...antenna,
                        power: powerInMW,
                        formattedPower: formattedPower,
                        rawData: {
                          ...antenna.rawData,
                          more: {
                            ...antenna.rawData?.more,
                            power: powerInMW.toString(),
                          },
                        },
                      }
                    : antenna
                ),
              })),
            }))
          );

          showNotification(
            `Potencia obtenida: Antena con ID ${antennaId} - ${formattedPower} dBm`,
            "success"
          );

          return {
            success: true,
            power: powerInMW,
            formattedPower: formattedPower,
            antennaId: response.antennaId,
            message: response.message,
          };
        } else {
          throw new Error(response.message || "Error al obtener potencia");
        }
      } catch (error) {
        console.error(`❌ Error al obtener potencia de antena ${antennaId}:`, error);
        showNotification(`❌ Error: ${error.message || "No se pudo obtener la potencia"}`, "error");
        return { success: false, error: error.message, antennaId: antennaIdStr };
      } finally {
        setGettingPower((prev) => ({ ...prev, [antennaIdStr]: false }));
      }
    },
    [showNotification, formatMWToDBm, checkAntennaCooldown]
  );

  const setAntennaPower = useCallback(
    async (antennaId, powerValue, readerType = "zebra") => {
      if (!antennaId) {
        showNotification("❌ ID de antena no válido", "error");
        return { success: false, error: "ID de antena no válido" };
      }

      if (powerValue === undefined || powerValue === null || isNaN(powerValue)) {
        showNotification("❌ Valor de potencia no válido", "error");
        return { success: false, error: "Valor de potencia no válido" };
      }

      const cooldownInfo = checkAntennaCooldown(antennaId);
      if (cooldownInfo) {
        showNotification(
          `El reader "${cooldownInfo.readerName}" está en tiempo de espera. Espere ${cooldownInfo.remainingMinutes} minutos antes de operar sus antenas.`,
          "warning"
        );

        return {
          success: false,
          error: "Reader on cooldown",
          cooldown: true,
          readerId: cooldownInfo.readerId,
          readerName: cooldownInfo.readerName,
        };
      }

      const antennaIdStr = antennaId.toString();
      setSettingPower((prev) => ({ ...prev, [antennaIdStr]: true }));

      try {
        const response = await setAntennaPowerService(antennaId, powerValue, readerType);

        if (response.success) {
          const powerInMW = response.power || powerValue;
          const formattedPower = response.formattedPower || formatMWToDBm(powerInMW);

          setAntennaPowers((prev) => ({
            ...prev,
            [antennaIdStr]: {
              power: powerInMW,
              formattedPower: formattedPower,
              timestamp: response.timestamp || new Date().toISOString(),
              antennaId: response.antennaId || antennaId,
              message: response.message || "Potencia configurada",
            },
          }));

          setReaderGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              readers: group.readers.map((reader) => ({
                ...reader,
                antennas: reader.antennas?.map((antenna) =>
                  antenna.id === antennaIdStr
                    ? {
                        ...antenna,
                        power: powerInMW,
                        formattedPower: formattedPower,
                        rawData: {
                          ...antenna.rawData,
                          more: {
                            ...antenna.rawData?.more,
                            power: powerInMW.toString(),
                            powerdate: new Date().toISOString(),
                            powerresponse: response.message || "Potencia configurada",
                          },
                        },
                      }
                    : antenna
                ),
              })),
            }))
          );

          setTimeout(() => {
            getAntennaPower(antennaId).catch((err) => {
              console.warn(`⚠️ No se pudo verificar potencia después de configurar:`, err);
            });
          }, 1000);

          showNotification(
            `Potencia configurada: Antena ${antennaId} - ${formattedPower} dBm`,
            "success"
          );

          return {
            success: true,
            power: powerInMW,
            formattedPower: formattedPower,
            antennaId: response.antennaId || antennaId,
            message: response.message || "Potencia configurada exitosamente",
          };
        } else {
          throw new Error(response.message || "Error al configurar potencia");
        }
      } catch (error) {
        console.error(`❌ Error al configurar potencia de antena ${antennaId}:`, error);
        showNotification(
          `❌ Error: ${error.message || "No se pudo configurar la potencia"}`,
          "error"
        );
        return { success: false, error: error.message, antennaId: antennaIdStr };
      } finally {
        setSettingPower((prev) => ({ ...prev, [antennaIdStr]: false }));
      }
    },
    [showNotification, formatMWToDBm, getAntennaPower, checkAntennaCooldown]
  );

  const getCurrentAntennaPower = useCallback(
    (antennaId) => {
      if (!antennaId) return null;
      return antennaPowers[antennaId.toString()] || null;
    },
    [antennaPowers]
  );

  // ============================================
  // FUNCIONES DE ESTADO DE ANTENAS (ENCENDIDA/APAGADA)
  // ============================================
  const getAntennaStatus = useCallback(
    async (antennaId) => {
      if (!antennaId) {
        showNotification("❌ ID de antena no válido", "error");
        return { success: false, error: "ID de antena no válido" };
      }

      const cooldownInfo = checkAntennaCooldown(antennaId);
      if (cooldownInfo) {
        showNotification(
          `El reader "${cooldownInfo.readerName}" está en tiempo de espera. Espere ${cooldownInfo.remainingMinutes} minutos antes de operar sus antenas.`,
          "warning"
        );

        return {
          success: false,
          error: "Reader on cooldown",
          cooldown: true,
          readerId: cooldownInfo.readerId,
          readerName: cooldownInfo.readerName,
        };
      }

      const antennaIdStr = antennaId.toString();
      setGettingStatus((prev) => ({ ...prev, [antennaIdStr]: true }));

      try {
        const response = await getAntennaStatusService(antennaId);

        if (response.success) {
          setAntennaStatuses((prev) => ({
            ...prev,
            [antennaIdStr]: {
              status: response.status,
              timestamp: response.timestamp,
              antennaId: response.antennaId,
              message: response.message,
            },
          }));

          setReaderGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              readers: group.readers.map((reader) => ({
                ...reader,
                antennas: reader.antennas?.map((antenna) =>
                  antenna.id === antennaIdStr
                    ? { ...antenna, statusFromApi: response.status }
                    : antenna
                ),
              })),
            }))
          );

          showNotification(`Estado de antena ${antennaId}: ${response.status}`, "success");

          return {
            success: true,
            status: response.status,
            antennaId: response.antennaId,
            message: response.message,
          };
        } else {
          throw new Error(response.message || "Error al obtener estado");
        }
      } catch (error) {
        console.error(`❌ Error al obtener estado de antena ${antennaId}:`, error);
        showNotification(`❌ Error: ${error.message || "No se pudo obtener el estado"}`, "error");
        return { success: false, error: error.message, antennaId: antennaIdStr };
      } finally {
        setGettingStatus((prev) => ({ ...prev, [antennaIdStr]: false }));
      }
    },
    [showNotification, checkAntennaCooldown]
  );

  const getAntennasStatusByReader = useCallback(async (antennaIds) => {
    if (!Array.isArray(antennaIds) || antennaIds.length === 0) {
      return { success: false, error: "No hay antenas para consultar" };
    }

    const loadingState = {};
    antennaIds.forEach((id) => {
      loadingState[id.toString()] = true;
    });
    setGettingStatus((prev) => ({ ...prev, ...loadingState }));

    try {
      const results = await Promise.allSettled(
        antennaIds.map(async (id) => {
          const response = await getAntennaStatusService(id);
          return { id, response };
        })
      );

      const newStatuses = {};
      const errors = [];

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          const { id, response } = result.value;
          if (response.success) {
            newStatuses[id.toString()] = {
              status: response.status,
              timestamp: response.timestamp,
              antennaId: response.antennaId,
              message: response.message,
            };
          } else {
            errors.push({ id, error: response.message });
          }
        } else {
          errors.push({ id: "unknown", error: result.reason?.message || "Error desconocido" });
        }
      });

      setAntennaStatuses((prev) => ({ ...prev, ...newStatuses }));

      setReaderGroups((prevGroups) =>
        prevGroups.map((group) => ({
          ...group,
          readers: group.readers.map((reader) => ({
            ...reader,
            antennas: reader.antennas?.map((antenna) => {
              const newStatus = newStatuses[antenna.id?.toString()];
              return newStatus ? { ...antenna, statusFromApi: newStatus.status } : antenna;
            }),
          })),
        }))
      );

      return { success: true, statuses: newStatuses, errors };
    } catch (error) {
      console.error("❌ Error al obtener estados múltiples:", error);
      return { success: false, error: error.message };
    } finally {
      const clearState = {};
      antennaIds.forEach((id) => {
        clearState[id.toString()] = false;
      });
      setGettingStatus((prev) => ({ ...prev, ...clearState }));
    }
  }, []);

  const getCurrentAntennaStatus = useCallback(
    (antennaId) => {
      if (!antennaId) return null;
      return antennaStatuses[antennaId.toString()] || null;
    },
    [antennaStatuses]
  );

  // ============================================
  // FUNCIÓN: ENCENDER / APAGAR ANTENA
  // ============================================
  const setAntennaPowerStatus = useCallback(
    async (antennaId, state) => {
      if (!antennaId) {
        showNotification("❌ ID de antena no válido", "error");
        return { success: false, error: "ID de antena no válido" };
      }

      if (state !== "on" && state !== "off") {
        showNotification("❌ Estado no válido", "error");
        return { success: false, error: "Estado no válido" };
      }

      const cooldownInfo = checkAntennaCooldown(antennaId);
      if (cooldownInfo) {
        showNotification(
          `El reader "${cooldownInfo.readerName}" está en tiempo de espera. Espere ${cooldownInfo.remainingMinutes} minutos antes de operar sus antenas.`,
          "warning"
        );

        return {
          success: false,
          error: "Reader on cooldown",
          cooldown: true,
          readerId: cooldownInfo.readerId,
          readerName: cooldownInfo.readerName,
        };
      }

      const antennaIdStr = antennaId.toString();
      setTogglingPower((prev) => ({ ...prev, [antennaIdStr]: true }));

      try {
        const response = await setAntennaPowerStatusService(antennaId, state);

        if (response.success) {
          const newStatus = response.newStatus;

          setReaderGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              readers: group.readers.map((reader) => ({
                ...reader,
                antennas: reader.antennas?.map((antenna) =>
                  antenna.id === antennaIdStr ? { ...antenna, statusFromApi: newStatus } : antenna
                ),
              })),
            }))
          );

          setAntennaStatuses((prev) => ({
            ...prev,
            [antennaIdStr]: {
              status: newStatus,
              timestamp: response.timestamp,
              antennaId: response.antennaId,
              message: response.message,
            },
          }));

          showNotification(
            `✅ Antena ${antennaId} ${state === "on" ? "encendida" : "apagada"} correctamente`,
            "success"
          );

          return {
            success: true,
            message: response.message,
            antennaId: response.antennaId,
            newStatus: newStatus,
          };
        } else {
          throw new Error(response.message || "Error al cambiar estado");
        }
      } catch (error) {
        console.error(`❌ Error al cambiar estado de antena ${antennaId}:`, error);
        showNotification(`❌ Error: ${error.message || "No se pudo cambiar el estado"}`, "error");
        return { success: false, error: error.message, antennaId: antennaIdStr };
      } finally {
        setTogglingPower((prev) => ({ ...prev, [antennaIdStr]: false }));
      }
    },
    [showNotification, checkAntennaCooldown]
  );

  // ============================================
  // FUNCIONES AUXILIARES
  // ============================================
  const refreshReaderData = useCallback(() => fetchReaders(), [fetchReaders]);

  useEffect(() => {
    setSelectedReader((previous) =>
      previous
        ? readerGroups.find((group) => group.id === previous.id && group.name === previous.name) ||
          readerGroups.find((group) => group.hostname === previous.hostname) ||
          null
        : null
    );
    setSelectedReaderElement((previous) =>
      previous
        ? readerGroups
            .flatMap((group) => group.readers)
            .find((reader) => reader.id === previous.id) || null
        : null
    );
  }, [readerGroups]);

  const selectReaderGroup = useCallback((readerGroup) => {
    setSelectedReader(readerGroup);
  }, []);

  const selectReaderElement = useCallback((reader) => {
    setSelectedReaderElement(reader);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedReader(null);
    setSelectedReaderElement(null);
  }, []);

  const getUniqueCommerces = useCallback(() => {
    const allReaders = readerGroups.flatMap((group) => group.readers);
    const uniqueCommerces = [
      ...new Set(allReaders.map((reader) => reader.commerce || "Sin comercio")),
    ];

    return uniqueCommerces.map((commerce) => {
      const commerceReaders = allReaders.filter((reader) => reader.commerce === commerce);
      const activeReaders = commerceReaders.filter((reader) => reader.status).length;

      return {
        name: commerce,
        totalReaders: commerceReaders.length,
        activeReaders: activeReaders,
        readers: commerceReaders,
      };
    });
  }, [readerGroups]);

  // ============================================
  // ESTADÍSTICAS CALCULADAS
  // ============================================
  const stats = useMemo(() => {
    const allReaders = readerGroups.flatMap((group) => group.readers);
    const activeReaders = allReaders.filter((reader) => reader.status);
    const lastUpdate = latestReaderUpdate(allReaders);

    const uniqueCommerces = [...new Set(allReaders.map((reader) => reader.commerce))];
    const totalAntennas = allReaders.reduce(
      (sum, reader) => sum + (reader.antennas?.length || 0),
      0
    );

    const activeAntennas = allReaders.reduce((sum, reader) => {
      const readerAntennas = reader.antennas || [];
      return sum + readerAntennas.filter((antenna) => antenna.status === "Activa").length;
    }, 0);

    const readersOnCooldown = allReaders.filter((reader) => reader.isOnCooldown).length;

    const antennasWithPower = allReaders.reduce((sum, reader) => {
      const readerAntennas = reader.antennas || [];
      return sum + readerAntennas.filter((antenna) => antenna.power !== null).length;
    }, 0);

    const edsGroups = readerGroups.filter((group) => group.name?.toUpperCase().startsWith("EDS"));
    const edsReadersCount = edsGroups.reduce((sum, group) => sum + group.activeReaders, 0);
    const edsReadersTotal = edsGroups.reduce((sum, group) => sum + group.totalReaders, 0);
    const edsInactiveCount = edsReadersTotal - edsReadersCount;
    const edsGroupsCount = edsGroups.length;

    return {
      totalGroups: readerGroups.length,
      totalReaders: allReaders.length,
      activeReaders: activeReaders.length,
      inactiveReaders: allReaders.length - activeReaders.length,
      uniqueCommerces: uniqueCommerces.length,
      totalAntennas,
      activeAntennas,
      antennasWithPower,
      lastUpdate: lastUpdate ? new Date(lastUpdate).toISOString() : null,
      readersOnCooldown,
      edsReadersCount,
      edsReadersTotal,
      edsInactiveCount,
      edsGroupsCount,
    };
  }, [readerGroups]);

  // ============================================
  // RETORNO DEL HOOK
  // ============================================
  const guardedResetAll = useCallback(
    (...args) => withReaderResetLock(() => resetAllReaders(...args)),
    [resetAllReaders]
  );
  const guardedResetEDS = useCallback(
    (...args) => withReaderResetLock(() => resetEDSReaders(...args)),
    [resetEDSReaders]
  );
  const guardedResetCommerce = useCallback(
    (...args) => withReaderResetLock(() => resetReaderByCommerce(...args)),
    [resetReaderByCommerce]
  );
  const guardedResetIndividual = useCallback(
    (...args) => withReaderResetLock(() => resetReaderById(...args)),
    [resetReaderById]
  );

  return {
    readerGroups,
    lastFetchedAt,
    loading,
    resetAllLoading,
    resetEDSLoading,
    resetCommerceLoading,
    resetIndividualLoading,
    sendingPulse,
    gettingPower,
    settingPower,
    gettingStatus,
    togglingPower,
    exporting,
    error,
    selectedReader,
    selectedReaderElement,
    notification,
    stats,
    resetCooldowns,
    antennaPowers,
    antennaStatuses,
    edsResetProgress,

    showNotification,
    closeNotification,

    fetchReaders,
    refreshReaderData,
    exportToExcel,
    exportAdvancedReport,
    resetAllReaders: guardedResetAll,
    resetEDSReaders: guardedResetEDS,
    resetReaderByCommerce: guardedResetCommerce,
    resetReaderById: guardedResetIndividual,

    selectReaderGroup,
    selectReaderElement,
    clearSelection,

    handleSendPulse,
    handleSendPulseToMultiple,
    getUniqueCommerces,

    isReaderOnCooldown,
    getRemainingCooldownTime,
    getReaderCooldownStatus,
    startCooldown,
    clearCooldown,
    getAllCooldownReaders,

    findReaderByAntennaId,
    checkAntennaCooldown,

    getAntennaPower,
    setAntennaPower,
    setAntennaPowerStatus,
    getCurrentAntennaPower,
    convertDBmToMW,
    formatMWToDBm,

    getAntennaStatus,
    getCurrentAntennaStatus,
    getAntennasStatusByReader,
  };
};
